/* ═══════════════════════════════════════════════════════════════
   Stilprofil eines Jahrgangs — relativ zur eigenen Region

   Wetter übersetzt sich ziemlich direkt in Stil. Ob ein Jahr „warm“
   war, hängt aber von der Region ab: Ein warmes Moseljahr wäre in
   Barossa ein kühles. Deshalb wird jeder Jahrgang an der Verteilung
   SEINER Region gemessen — dieselbe Überlegung wie beim
   Jahrgangsvergleich.

   Gemessen wird über Rangplätze (Perzentile), nicht über z-Werte.
   Grund ist das Bodenwasser: Es stößt in trockenen Jahren auf 0 mm,
   die Verteilung ist dort abgeschnitten, und ein z-Wert würde ein
   halbes Dutzend identischer Nullen als „durchschnittlich trocken“
   ausweisen. Ein Rang behandelt Gleichstände sauber (Mittelrang) und
   ist gegen Ausreißer unempfindlich.

   Vier Achsen, aus der Géoviticulture-Logik abgeleitet:

     Wärme         Temperatur + Gradtage       → Reife, Körper, Alkohol, Säure
     Nächte        Tmin der 30 Tage vor Reife,  → Frische, Aromatik, Farbe
                   gemessen an der Normalen
                   dieser Tage (ab Fassung 5)
     Reiferegen    Regen rund um die Reife     → Verdünnung, Fäulnisdruck
     Bodenwasser   Bilanz am Tag der Reife     → Stress, Beerengröße, Konzentration

   Der Reifetermin wird je Jahr aus der Temperatursumme geschätzt
   (`reifephasen` in wetter.js, ab Klimareihe Fassung 3). Vorher galten
   September und Oktober fest als Reife — im Hitzejahr ist die Reife
   aber oft im August erreicht, und der Herbstregen fiel danach.

   TRENDBEREINIGUNG (Wärme und Nächte). Die Reihe reicht bis 1950
   zurück, und seither ist die Vegetationsperiode an der Mosel rund
   1,5 Grad wärmer geworden. Gemessen an allen Jahren war deshalb fast
   jedes Jahr nach 2000 „warm“ oder „typisch“ — 2010, 2013 oder 2021,
   in ihrer Zeit klar kühle Jahrgänge, lagen im Mittelfeld der
   Fünfzigerjahre. Wer einen Jahrgang einordnet, vergleicht aber mit
   den Jahrgängen seiner Zeit. Wärme und Nächte werden deshalb als
   Abstand zum gleitenden Klimatrend gerankt (lokal lineare
   Regression, ±20 Jahre). Regen und Bodenwasser haben keinen
   vergleichbaren Trend und bleiben unbereinigt. Unter 30 Jahren
   Reihe wird nicht bereinigt — der Trend wäre nicht zu trennen.

   Das Profil ist eine Deutung des WETTERS, keine Verkostungsnotiz.
   Lesezeitpunkt, Ertragsregulierung und Kellerarbeit können jede
   dieser Tendenzen verstärken oder aufheben.
   ═══════════════════════════════════════════════════════════════ */

import { kuehleNaechte, mitReifephase } from "./jahrgangsvergleich.js";

const zahl = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/* Stufenworte je Achse, von sehr niedrig bis sehr hoch. */
const ACHSEN = [
  { schluessel: "waerme", label: "Wärme",
    stufen: ["sehr kühl", "kühl", "typisch", "warm", "sehr warm"] },
  { schluessel: "naechte", label: "Nächte in der Reife",
    stufen: ["sehr kühl", "kühl", "typisch", "mild", "sehr mild"] },
  { schluessel: "reifeRegen", label: "Regen in der Reife",
    stufen: ["sehr trocken", "trocken", "typisch", "feucht", "sehr nass"] },
  { schluessel: "bodenwasser", label: "Bodenwasser am Ende",
    stufen: ["ausgetrocknet", "knapp", "typisch", "reichlich", "sehr reichlich"] },
];

/* Grenzen der fünf Stufen auf der Rangskala 0–1. Das mittlere Drittel
   heißt „typisch“ — sonst wäre fast jedes Jahr irgendwo auffällig. */
const GRENZEN = [0.15, 0.35, 0.65, 0.85];

const stufeVon = (rang) => {
  if (rang == null) return null;
  let i = 0;
  while (i < GRENZEN.length && rang > GRENZEN[i]) i++;
  return i;   /* 0 = sehr niedrig … 4 = sehr hoch */
};


/** Rangplatz eines Werts in einer Menge, 0–1, Gleichstände gemittelt. */
function rangIn(werte, x) {
  const da = werte.filter((v) => v !== null);
  if (x === null || da.length < 5) return null;
  let kleiner = 0, gleich = 0;
  for (const v of da) { if (v < x) kleiner++; else if (v === x) gleich++; }
  /* Mittelrang, auf 0–1 gezogen: kleinster Wert → nahe 0, größter → nahe 1. */
  return (kleiner + (gleich - 1) / 2) / Math.max(1, da.length - 1);
}


/** Die Achsenwerte eines Jahres aus der Klimareihe. */
function achsenWerte(j) {
  return {
    jahr: j.jahr,
    tMittel: zahl(j.tMittel), gdd: zahl(j.gdd),
    naechte: zahl(j.reifeNacht),
    reifeRegen: zahl(j.reifeRegen),
    bodenwasser: zahl(j.di),
  };
}


/* Halbe Fensterbreite des Trends in Jahren und Mindestlänge der Reihe. */
const TREND_HALBBREITE = 20;
const TREND_MIN_JAHRE = 30;

/** Abstand jedes Werts zum gleitenden Trend — lokal lineare Regression
    mit Tricube-Gewichten. Lokal linear statt gleitendem Mittel, weil
    das Mittel an den Rändern nachhinkt: Für 2024 läge es bei 2014 und
    machte jedes jüngste Jahr „warm“. Reihen unter `TREND_MIN_JAHRE`
    kommen unverändert zurück; fehlende Werte bleiben `null`. */
function trendRest(jahre, werte, halbbreite = TREND_HALBBREITE) {
  const punkte = jahre.map((x, i) => [x, werte[i]]).filter(([x, y]) => x != null && y != null);
  if (punkte.length < TREND_MIN_JAHRE) return werte;
  return werte.map((y, i) => {
    if (y == null) return null;
    const x0 = jahre[i];
    let sw = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
    for (const [x, v] of punkte) {
      const d = Math.abs(x - x0) / (halbbreite + 1);
      if (d >= 1) continue;
      const w = (1 - d ** 3) ** 3;
      const dx = x - x0;
      sw += w; sx += w * dx; sy += w * v; sxx += w * dx * dx; sxy += w * dx * v;
    }
    const nenner = sw * sxx - sx * sx;
    /* Achsenabschnitt bei x0 = Trendwert dieses Jahres. */
    const trend = Math.abs(nenner) < 1e-9 ? sy / sw : (sy * sxx - sx * sxy) / nenner;
    return y - trend;
  });
}


/**
 * Stilprofil eines Jahrgangs.
 *
 * @returns `null`, wenn das Jahr nicht in der Reihe steht, sonst
 *   `{ jahr, achsen: [{ schluessel, label, rang, lage, stufe, wort }],
 *      merkmale: [Satz, …], kurz }`.
 *   `stufe` ist 0–4, `rang` 0–1 (Anteil der Jahre darunter).
 */
function stilprofil(klima, jahr) {
  if (!klima || !Array.isArray(klima.jahre)) return null;
  /* Nächte als Abweichung von der Normalen derselben Tage (ab
     Klimareihe Fassung 5), wenn JEDES Jahr sie trägt — Ränge aus
     Abweichungen und Absolutwerten gemischt wären sinnlos. Absolut
     gemessen hing die Achse fast nur am Reifetermin (frühe Reife =
     Augustnächte = „mild“): An erzeugten Mosel-Reihen folgte die
     Nächtestufe der Wärmestufe mit r = 0,77–0,88, und „warme Tage,
     kühle Nächte“ kam in 76 Jahren null- bis einmal vor. Als
     Abweichung r = 0,40–0,66 und ein- bis fünfmal (acht Saaten). */
  const nachtAlsAbw = klima.jahre.length > 0 && klima.jahre.every((j) => zahl(j.nachtAbw) !== null);
  const jahre = klima.jahre.map((j) => {
    /* Reife am geschätzten Termin (Serverfelder ab Fassung 3), sonst
       Rückfall auf die Kalendermonate. Dieselbe Definition wie im
       Jahrgangsvergleich. */
    const mitReife = mitReifephase(j, klima.monateJeJahr);
    const di = zahl(j.diLese) ?? zahl(mitReife.di)
      ?? (klima.diJeJahr ? zahl(klima.diJeJahr[j.jahr]) : null);
    const nacht = nachtAlsAbw
      ? zahl(j.nachtAbw)
      : zahl(mitReife.reifeNacht) ?? kuehleNaechte(klima.monateJeJahr, j.jahr);
    return achsenWerte({ ...mitReife, di, reifeNacht: nacht });
  });
  const idx = klima.jahre.findIndex((j) => j.jahr === jahr);
  if (idx < 0) return null;
  const selbst = jahre[idx];

  const spalte = (feld) => jahre.map((w) => w[feld]);
  const jahrSpalte = spalte("jahr");
  const bereinigt = (feld) => trendRest(jahrSpalte, spalte(feld));
  const trendbereinigt = jahrSpalte.filter((x) => x != null).length >= TREND_MIN_JAHRE;

  /* Wärme aus zwei Größen. Das Mittel ZWEIER Ränge streut enger als
     ein einzelner Rang — die Ränder 0,15 und 0,85 wurden dadurch
     seltener erreicht als gedacht. Deshalb wird das Mittel über alle
     Jahre noch einmal gerankt; dann ist „sehr kühl“ wieder genau das
     kühlste Siebtel. */
  const tR = bereinigt("tMittel"), gR = bereinigt("gdd");
  const waermeJeJahr = jahre.map((_, i) => {
    const rT = rangIn(tR, tR[i]);
    const rG = rangIn(gR, gR[i]);
    return rT != null && rG != null ? (rT + rG) / 2 : (rT ?? rG);
  });
  const nR = bereinigt("naechte");
  const raenge = {
    waerme: rangIn(waermeJeJahr, waermeJeJahr[idx]),
    naechte: rangIn(nR, nR[idx]),
    reifeRegen: rangIn(spalte("reifeRegen"), selbst.reifeRegen),
    bodenwasser: rangIn(spalte("bodenwasser"), selbst.bodenwasser),
  };

  /* Bodenwasser genau 0: Der Boden war leer — das sagt mehr als jeder
     Rang, weil sich dort viele Jahre drängen und den Mittelrang teilen.
     Ohne diese Ausnahme stand bei 0 mm „knapp“.
     ABER nur, wo leer die Ausnahme ist. In Napa endete die Reife an
     echten Reihen in 21 von 26 Jahren seit 2000 bei 0 mm, in Barossa in
     18 von 27 — dort hieß jedes Jahr „ausgetrocknet“ mit „Trockenstress“,
     also gar nichts. Ist der Boden in mindestens der Hälfte der Jahre
     leer, ist leer der Normalfall der Region: Ein leeres Jahr heißt
     dann „typisch“ (Rang 0,5), und nur die Jahre mit Restwasser werden
     untereinander in die obere Hälfte gerankt. Der Mittelrang der
     Nullen allein hätte bei 70 % leeren Jahren 0,34 ergeben — „knapp“
     samt Trockenstress, wieder in fast jedem Jahr. */
  const boden = spalte("bodenwasser").filter((v) => v !== null);
  const leerUeblich = boden.length > 0 && boden.filter((v) => v === 0).length >= boden.length / 2;
  const bodenLeer = selbst.bodenwasser === 0 && !leerUeblich;
  if (leerUeblich && selbst.bodenwasser != null) {
    const rest = rangIn(boden.filter((v) => v > 0), selbst.bodenwasser);
    raenge.bodenwasser = selbst.bodenwasser === 0 ? 0.5 : 0.5 + 0.5 * (rest ?? 0.5);
  }

  /* Ab Fassung 3 ist das Bodenwasser am Reifetag gemessen, davor am
     Fensterende — das Etikett sagt, was gezeigt wird. */
  const amReifetag = zahl(klima.jahre[idx].diLese) !== null;
  const achsen = ACHSEN.map((a) => {
    const rang = raenge[a.schluessel];
    const leer = a.schluessel === "bodenwasser" && bodenLeer;
    const stufe = leer ? 0 : stufeVon(rang);
    return {
      schluessel: a.schluessel,
      label: a.schluessel === "bodenwasser" && amReifetag ? "Bodenwasser zur Reife" : a.label,
      rang: rang == null ? null : +rang.toFixed(2),
      /* Lage der Marke auf dem Balken. Meist der Rang — bei leerem
         Boden aber ganz links: Der Mittelrang der vielen Nullen stünde
         sonst neben dem Wort „ausgetrocknet“ mitten im Balken. */
      lage: rang == null ? null : (leer ? 0 : +rang.toFixed(2)),
      stufe, wort: stufe == null ? null : a.stufen[stufe],
    };
  });
  const s = Object.fromEntries(achsen.map((a) => [a.schluessel, a.stufe]));

  return {
    jahr, achsen, merkmale: deute(s, bodenLeer), kurz: kurzfassung(achsen),
    trendbereinigt,
    /* Geschätzter Reifetermin (ISO) und der Mittelwert der Region
       ("MM-TT") — nur aus Klimareihen ab Fassung 3. */
    lese: typeof klima.jahre[idx].lese === "string" ? klima.jahre[idx].lese : null,
    leseMittel: typeof klima.leseMittel === "string" ? klima.leseMittel : null,
  };
}


/* ── Deutung ──────────────────────────────────────────────────────
   Erst die Zusammenspiele, die in der Literatur einen eigenen Namen
   haben — sie sind das, woran man einen Jahrgang erkennt —, dann die
   Einzelaussagen je Achse.

   GEPRÜFT an Bewertungen (28.10.2026, Händlerkatalog, 16 Gebiete,
   trendbereinigte Ränge, `jahrgangs-kalibrierung.js --ratings`):
   Jahre mit „Regen zur Reife“ lagen im Mittel auf Rang 0,40 (p 0,003),
   „Trockene Reife“ 0,57 (p < 0,001), „Trockenstress“ 0,57 (p 0,007),
   „Hitzejahr“ 0,60 (p 0,07); mehr Bodenwasser zur Reife ging mit
   schwächeren Bewertungen einher (r = −0,26). Die Sätze dazu stimmen
   also. „Reife mit Frische“ lag bei 0,50 (p 0,95, n = 13) — der Zusatz
   „das Profil großer Jahrgänge“ war eine Qualitätsaussage ohne Beleg
   und ist gestrichen; die Stilaussage bleibt. */
function deute(s, bodenLeer) {
  const hoch = (x) => x != null && x >= 3;
  const tief = (x) => x != null && x <= 1;
  const raus = [];

  /* Extreme Wärme plus Trockenheit geht vor: Säure wird in der
     Sommerhitze abgebaut, kühle Nächte im September holen sie nicht
     zurück. Die erste Fassung erklärte 2003 zum „Profil großer
     Jahrgänge“, weil sie warm und extrem nicht unterschied. */
  const hitze = s.waerme === 4 && (tief(s.reifeRegen) || bodenLeer);
  if (hitze) {
    raus.push("Hitzejahr: opulent und alkoholreich, die Säure wird knapp");
  } else if (s.waerme === 3 && tief(s.naechte)) {
    raus.push("Reife mit Frische: warme Tage, kühle Nächte — volle Reife bei erhaltener Säure");
  }
  if (tief(s.waerme) && hoch(s.reifeRegen)) {
    raus.push("Kühl und nass: schlank, säurebetont, stark von der Selektion abhängig");
  }

  if (hoch(s.waerme)) raus.push("Volle Reife, mehr Körper und Alkohol, weichere Säure");
  else if (tief(s.waerme)) raus.push("Schlank und säurebetont, eher niedriger Alkohol");

  if (tief(s.naechte)) raus.push("Kühle Nächte bewahren Frische, Aromatik und Farbe");
  else if (hoch(s.naechte)) raus.push("Milde Nächte: weichere Frucht, Aromatik eher reif als frisch");

  if (hoch(s.reifeRegen)) raus.push("Regen zur Reife: Verdünnungs- und Fäulnisgefahr");
  else if (tief(s.reifeRegen)) raus.push("Trockene Reife: gesundes Lesegut");

  if (bodenLeer || tief(s.bodenwasser)) {
    raus.push("Trockenstress: kleine Beeren, dicke Schalen, Konzentration und Tannin");
  } else if (hoch(s.bodenwasser)) {
    raus.push("Gute Wasserversorgung: größere Beeren, zugänglicher Stil");
  }

  if (raus.length === 0) raus.push("Ein für die Region typischer Jahrgang ohne ausgeprägte Richtung");
  return raus;
}


/** Einzeiler aus den auffälligen Achsen. */
function kurzfassung(achsen) {
  const auffaellig = achsen.filter((a) => a.stufe != null && a.stufe !== 2);
  if (auffaellig.length === 0) return "typisch für die Region";
  return auffaellig.map((a) => `${a.label}: ${a.wort}`).join(" · ");
}


export { ACHSEN, GRENZEN, TREND_MIN_JAHRE, rangIn, stilprofil, stufeVon, trendRest };
